"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Attachment = exports.ImageNotFound = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const styled_components_1 = __importDefault(require("styled-components"));
const useProgressiveImg_1 = require("../../shared/hooks/useProgressiveImg");
const icons_1 = require("../../atoms/icons");
const typography_1 = require("../../shared/typography");
const faExclamationTriangle_1 = require("@fortawesome/pro-solid-svg-icons/faExclamationTriangle");
const react_i18next_1 = require("react-i18next");
const faRedo_1 = require("@fortawesome/pro-regular-svg-icons/faRedo");
const AttachmentModal_1 = require("./AttachmentModal");
const Spinner_1 = require("../../molecules/loading/Spinner");
const ImageLoadingError = ({ width, height, translation, onClick }) => {
    return ((0, jsx_runtime_1.jsx)(ImageError, { className: "error", height: height, width: width, children: (0, jsx_runtime_1.jsxs)("div", { children: [(0, jsx_runtime_1.jsx)(icons_1.Icon, { shape: "rectangle", color: "grey", icon: faExclamationTriangle_1.faExclamationTriangle }, void 0), (0, jsx_runtime_1.jsx)(typography_1.P, { children: translation.errorMessage }, void 0), (0, jsx_runtime_1.jsx)(typography_1.P, { onClick: onClick, children: translation.tryAgain }, void 0)] }, void 0) }, void 0));
};
const ImageError = styled_components_1.default.div `
  align-items: center;
  display: flex;
  flex-direction: column;
  height: ${({ height }) => height}px;
  min-height: 150px;
  min-width: 150px;
  justify-content: center;
  width: ${({ width }) => width}px;

  p {
    line-height: 1.5rem;
    margin-bottom: 0;
  }

  & > div {
    text-align: center;

    & > div {
      margin-bottom: ${({ theme }) => theme.spacings.xs};
    }
  }

  p + p {
    cursor: pointer;
    text-decoration: underline;
  }
`;
const ImageNotFound = ({ translation }) => {
    return (0, jsx_runtime_1.jsx)(NotFound, { children: translation }, void 0);
};
exports.ImageNotFound = ImageNotFound;
const NotFound = styled_components_1.default.div `
  align-items: center;
  background-color: ${({ theme }) => theme.colors.silverLighten70};
  border-radius: ${({ theme }) => theme.utils.radius};
  display: flex;
  height: 243px;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacings.xs};
`;
exports.Attachment = (0, react_1.memo)(({ attachment, onRedoClick, isError, isOffline, modalLabel }) => {
    const { t } = (0, react_i18next_1.useTranslation)();
    const [src, { isBlur }] = (0, useProgressiveImg_1.useProgressiveImg)(attachment.sizes.thumbnail.url, attachment.sizes.medium.url);
    const attachmentRef = (0, react_1.createRef)();
    const [dimensions, setDimensions] = (0, react_1.useState)({
        width: 0,
        height: 0
    });
    const [isImageLoadingError, setImageLoadingError] = (0, react_1.useState)(false);
    const [isModalOpen, setIsModalOpen] = (0, react_1.useState)(false);
    const [maxAttachmentHeight, setMaxAttachmentHeight] = (0, react_1.useState)(0);
    const [thumbnailZoom, setThumbnailZoom] = (0, react_1.useState)(1);
    const [isResending, setIsResending] = (0, react_1.useState)(false);
    (0, react_1.useEffect)(() => {
        var _a, _b, _c;
        let scale = (((_a = attachmentRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) || 0) /
            attachment.sizes.medium.size.width;
        if (scale > 1) {
            scale = 1;
        }
        setMaxAttachmentHeight((((_b = attachmentRef.current) === null || _b === void 0 ? void 0 : _b.offsetWidth) || 0) * 1.8);
        setThumbnailZoom(attachment.sizes.medium.size.width <=
            (((_c = attachmentRef.current) === null || _c === void 0 ? void 0 : _c.offsetWidth) || 0)
            ? 1
            : 10);
        setDimensions({
            width: attachment.sizes.medium.size.width,
            height: scale * attachment.sizes.medium.size.height
        });
    }, [isError]);
    const onErrorImageLoad = () => {
        setImageLoadingError(true);
    };
    const onLoadAgainClick = () => setImageLoadingError(false);
    const onImageClick = () => {
        if (isError)
            return;
        setIsModalOpen(true);
    };
    const onCloseModal = () => setIsModalOpen(false);
    const handleRedoClick = async () => {
        if (isResending)
            return;
        try {
            setIsResending(true);
            await onRedoClick();
        }
        finally {
            setIsResending(false);
        }
    };
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [isModalOpen && ((0, jsx_runtime_1.jsx)(AttachmentModal_1.AttachmentModal, { onClose: onCloseModal, attachment: attachment, label: modalLabel }, void 0)), (0, jsx_runtime_1.jsxs)(AttachmentWrapper, { className: "attachment", ref: attachmentRef, isBlur: isBlur, width: dimensions === null || dimensions === void 0 ? void 0 : dimensions.width, isOffline: isOffline, isError: isError, maxAttachmentHeight: maxAttachmentHeight, thumbnailZoom: thumbnailZoom, isResending: isResending, children: [(0, jsx_runtime_1.jsxs)("div", { children: [dimensions && isImageLoadingError ? ((0, jsx_runtime_1.jsx)(ImageLoadingError, { height: dimensions.height, width: dimensions.width, translation: {
                                    errorMessage: t("common.errors.unableToLoadImage"),
                                    tryAgain: t("common.errors.tryAgain")
                                }, onClick: onLoadAgainClick }, void 0)) : ((0, jsx_runtime_1.jsx)("img", { src: src, onClick: onImageClick, onError: onErrorImageLoad }, void 0)), isBlur && !isImageLoadingError && (0, jsx_runtime_1.jsx)(Spinner_1.Spinner, {}, void 0), isError && ((0, jsx_runtime_1.jsx)(RedoIcon, { className: "redo", shape: "rectangle", icon: faRedo_1.faRedo, onClick: handleRedoClick, isResending: isResending }, void 0))] }, void 0), isError && ((0, jsx_runtime_1.jsxs)(typography_1.P, { children: [(0, jsx_runtime_1.jsx)(icons_1.Icon, { icon: faExclamationTriangle_1.faExclamationTriangle, shape: "rectangleSmall", color: "pink" }, void 0), t("common.errors.imageNotSent")] }, void 0))] }, void 0)] }, void 0));
});
const RedoIcon = (0, styled_components_1.default)(icons_1.Icon) `
  & > svg {
    animation: ${({ isResending }) => isResending ? Spinner_1.rotateAnimation : "none"}
      infinite 1s;
  }
`;
const getObjectFit = (isBlur, thumbnailZoom, isError) => {
    if (isError) {
        return "cover";
    }
    if (!isBlur) {
        return "none";
    }
    return thumbnailZoom === 1 ? "cover" : "none";
};
const getBlur = (isBlur, thumbnailZoom) => {
    if (!isBlur) {
        return 0;
    }
    return thumbnailZoom === 1 ? 4 : 1;
};
const AttachmentWrapper = styled_components_1.default.div `
  overflow: hidden;

  & > div:first-of-type {
    position: relative;

    & > img {
      cursor: ${({ isError }) => (isError ? "unset" : "pointer")};
      filter: blur(
        ${({ isBlur, thumbnailZoom }) => getBlur(isBlur, thumbnailZoom)}px
      );
      margin: 0 auto;

      max-height: ${({ maxAttachmentHeight }) => maxAttachmentHeight}px;
      width: ${({ width }) => width || 0}px;
      transition: filter 0.3s linear;

      object-fit: ${({ isBlur, thumbnailZoom, isError }) => getObjectFit(isBlur, thumbnailZoom, isError)};
      object-position: center center;
      transform: scale(
        ${({ isBlur, thumbnailZoom }) => (isBlur ? thumbnailZoom : 1)}
      );
    }

    & > div:not(.error):not(.image) {
      cursor: ${({ isResending }) => isResending ? "not-allowed" : "pointer"};
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);

      & > svg {
        color: #8a94a2;
        cursor: inherit;
      }
    }
  }

  & > p {
    color: ${({ theme, isOffline }) => isOffline ? theme.colors.yellowBase : theme.colors.pinkBase};
    font-size: 0.75rem;
    margin: ${({ theme }) => `${theme.spacings.s} 0 0`};

    & svg {
      background-color: ${({ theme, isOffline }) => isOffline ? theme.colors.yellowLighten80 : theme.colors.silverBase};
      color: ${({ theme, isOffline }) => isOffline ? theme.colors.yellowBase : theme.colors.pinkBase};
    }

    & > div {
      margin-right: ${({ theme }) => theme.spacings.xs};
      font-size: 0.875rem;
    }
  }
`;
//# sourceMappingURL=Attachment.js.map